import * as React from 'react';

// useDataGridUrlState — owns the canonical state for the data grid
// (sorting, per-column filters, page index) and binds it to the URL
// query string when `persist` is true.
//
// URL conventions:
//   ?sort=name,-site         (asc default, '-' prefix = desc, comma-separated)
//   ?filter[name]=braga      (per-column filter, bracketed key)
//   ?filter[kind]=electric,water  (select values are comma-separated)
//   ?filter[value]=100..500  (range types use '..' as separator)
//   ?page=2                  (1-indexed; stored as pageIndex 0-indexed in state)
//   ?hide=type,site          (comma-separated column keys hidden by the user)
//   ?hide=                   (present but empty — the user explicitly chose
//                            "show everything", overriding any
//                            `visible: false` defaults on columns)
//
// Presence of `?hide=` (even empty) is the "user touched the popover"
// signal — once present, the URL is the sole source of truth for column
// visibility. Absent → fall back to per-column `visible` defaults.
//
// The hook reads the URL once on mount, then drives the URL forward as
// state changes. Cross-component / outside navigation that mutates the
// URL is not re-read — sessions are expected to stay within a single
// grid mount.

const SORT_PARAM = 'sort';
const PAGE_PARAM = 'page';
const HIDE_PARAM = 'hide';
const FILTER_PARAM_PATTERN = /^filter\[(.+)\]$/;

const parseSortParam = value => {
    if (!value) {
        return [];
    }
    return value
        .split(',')
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0)
        .map(entry => {
            if (entry.startsWith('-')) {
                return { id: entry.slice(1), desc: true };
            }
            return { id: entry, desc: false };
        });
};

const serializeSortParam = sorting => {
    if (!sorting || sorting.length === 0) {
        return null;
    }
    return sorting.map(({ id, desc }) => `${desc ? '-' : ''}${id}`).join(',');
};

const parseFiltersFromParams = params => {
    const filters = [];
    params.forEach((value, key) => {
        const match = key.match(FILTER_PARAM_PATTERN);
        if (!match) {
            return;
        }
        const columnId = match[1];
        filters.push({ id: columnId, value });
    });
    return filters;
};

const writeFiltersToParams = (params, columnFilters) => {
    // Drop every existing `filter[…]` key first so the URL only carries the
    // current filter set — stale entries from previous interactions would
    // otherwise survive and silently re-apply on the next read.
    Array.from(params.keys()).forEach(key => {
        if (FILTER_PARAM_PATTERN.test(key)) {
            params.delete(key);
        }
    });
    columnFilters.forEach(({ id, value }) => {
        const serialized = serializeFilterValue(value);
        if (serialized !== null) {
            params.set(`filter[${id}]`, serialized);
        }
    });
};

const serializeFilterValue = value => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return null;
        }
        return value.join(',');
    }
    if (typeof value === 'object') {
        // Range values: { from, to } → "from..to" (either side can be empty)
        const from = value.from ?? '';
        const to = value.to ?? '';
        if (from === '' && to === '') {
            return null;
        }
        return `${from}..${to}`;
    }
    return String(value);
};

// Parse `?hide=type,site` into a Set of column keys. The value comes in
// as a (possibly empty) string when the param is present; the empty
// string is a valid "user shows everything" signal and yields an empty
// set. Stale entries pointing at removed columns are kept in the set
// here — DataGrid silently ignores them when building the visibility
// map, and the next user toggle rewrites the URL minus the stale key.
const parseHideValue = value => new Set(
    value
        .split(',')
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0)
);

// Serialize the hidden set back to URL value form. Always returns a
// string (possibly empty) — the caller decides whether to write the
// param. We never return `null` here because emptiness is a valid value
// for the param ("user override, nothing hidden"); the *absence* of the
// param is a separate signal owned by `hiddenOverridden`.
const serializeHideValue = hiddenColumns => {
    if (!hiddenColumns || hiddenColumns.size === 0) {
        return '';
    }
    return [...hiddenColumns].join(',');
};

const readUrlState = (defaultSort, defaultPageIndex) => {
    if (typeof window === 'undefined') {
        return {
            sorting: defaultSort,
            columnFilters: [],
            pageIndex: defaultPageIndex,
            hiddenColumns: new Set(),
            hiddenOverridden: false
        };
    }
    const params = new URLSearchParams(window.location.search);
    const sortRaw = params.get(SORT_PARAM);
    const pageRaw = params.get(PAGE_PARAM);
    // Distinguish "no `?hide=` param at all" (defaults apply) from
    // "`?hide=` present with an empty value" (user override, nothing
    // hidden right now). `params.has` returns true for both `?hide=A,B`
    // and the bare `?hide=`; `params.get` returns `''` for the latter
    // and `null` when the key is absent entirely. We need both.
    const hideOverridden = params.has(HIDE_PARAM);
    return {
        sorting: sortRaw !== null ? parseSortParam(sortRaw) : defaultSort,
        columnFilters: parseFiltersFromParams(params),
        pageIndex: pageRaw !== null ? Math.max(0, Number.parseInt(pageRaw, 10) - 1) : defaultPageIndex,
        hiddenColumns: hideOverridden ? parseHideValue(params.get(HIDE_PARAM) ?? '') : new Set(),
        hiddenOverridden: hideOverridden
    };
};

const writeUrlState = ({ sorting, columnFilters, pageIndex, hiddenColumns, hiddenOverridden }) => {
    if (typeof window === 'undefined') {
        return;
    }
    const params = new URLSearchParams(window.location.search);

    const sortSerialized = serializeSortParam(sorting);
    if (sortSerialized === null) {
        params.delete(SORT_PARAM);
    } else {
        params.set(SORT_PARAM, sortSerialized);
    }

    writeFiltersToParams(params, columnFilters);

    if (pageIndex > 0) {
        params.set(PAGE_PARAM, String(pageIndex + 1));
    } else {
        params.delete(PAGE_PARAM);
    }

    // Override flag wins: once the user has touched the popover we keep
    // `?hide=` in the URL even if the value collapses to empty (= "user
    // explicitly shows everything"). Without the flag we wouldn't be
    // able to distinguish that state from "fresh load, defaults apply"
    // on a refresh.
    if (hiddenOverridden) {
        params.set(HIDE_PARAM, serializeHideValue(hiddenColumns));
    } else {
        params.delete(HIDE_PARAM);
    }

    const search = params.toString();
    const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', next);
};

// `defaultSort` is the URL-shape string array — `'name'` for asc, `'-site'`
// for desc. We convert to TanStack's `{ id, desc }` shape here so the rest
// of the hook and useReactTable work uniformly.
const normalizeDefaultSort = defaultSort =>
    defaultSort.map(entry => {
        if (typeof entry === 'string') {
            if (entry.startsWith('-')) {
                return { id: entry.slice(1), desc: true };
            }
            return { id: entry, desc: false };
        }
        return entry;
    });

const useDataGridUrlState = ({ persist, defaultSort, defaultPageIndex }) => {
    const normalizedDefaultSort = React.useMemo(
        () => normalizeDefaultSort(defaultSort),
        // Default sort is frozen on mount — the URL takes over after that.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const [state, setState] = React.useState(() =>
        persist
            ? readUrlState(normalizedDefaultSort, defaultPageIndex)
            : {
                sorting: normalizedDefaultSort,
                columnFilters: [],
                pageIndex: defaultPageIndex,
                hiddenColumns: new Set(),
                hiddenOverridden: false
            }
    );

    const setSorting = React.useCallback(nextSorting => {
        setState(previous => {
            const next = { ...previous, sorting: nextSorting, pageIndex: 0 };
            if (persist) {
                writeUrlState(next);
            }
            return next;
        });
    }, [persist]);

    const setColumnFilters = React.useCallback(nextFilters => {
        setState(previous => {
            const next = { ...previous, columnFilters: nextFilters, pageIndex: 0 };
            if (persist) {
                writeUrlState(next);
            }
            return next;
        });
    }, [persist]);

    const setPageIndex = React.useCallback(nextPageIndex => {
        setState(previous => {
            const next = { ...previous, pageIndex: nextPageIndex };
            if (persist) {
                writeUrlState(next);
            }
            return next;
        });
    }, [persist]);

    // Hidden-columns setter. Accepts a Set so callers don't have to
    // worry about uniqueness — the URL serializer also de-dupes via
    // the set iteration. Toggling a single column lives in the DataGrid;
    // this hook stays neutral on per-column semantics.
    //
    // Every call latches `hiddenOverridden = true` — the user has now
    // expressed an opinion about column visibility, and the URL takes
    // over from any `visible: false` defaults from then on. There is no
    // setter to clear the flag in v1; a "reset to defaults" action
    // would land here as `(new Set(), false)`.
    const setHiddenColumns = React.useCallback(nextHiddenColumns => {
        setState(previous => {
            const next = {
                ...previous,
                hiddenColumns: nextHiddenColumns,
                hiddenOverridden: true
            };
            if (persist) {
                writeUrlState(next);
            }
            return next;
        });
    }, [persist]);

    return {
        sorting: state.sorting,
        columnFilters: state.columnFilters,
        pageIndex: state.pageIndex,
        hiddenColumns: state.hiddenColumns,
        hiddenOverridden: state.hiddenOverridden,
        setSorting,
        setColumnFilters,
        setPageIndex,
        setHiddenColumns
    };
};

export default useDataGridUrlState;
